import type { FC, ReactNode, TouchEvent } from 'react'
import type { GalleryItem } from '@/types'

import { useEffect, useRef } from 'react'
import {
    ChevronLeft,
    ChevronRight,
    X
} from 'lucide-react'
import { getDisplayRatio } from '@/lib/photos'
import { useLockScroll } from '@/hooks/use-lock-scroll'
import MemoryPhoto from '@/components/landing/memory-photo'
import { GALLERY } from '@/constants/birthday'

interface LightboxProps {
    items: GalleryItem[]
    index: number | null
    onChange: (index: number | null) => void
}

const Lightbox: FC<LightboxProps> = ({ items, index, onChange }): ReactNode => {
    const dialogRef = useRef<HTMLDialogElement>(null)
    const touchStart = useRef<number | null>(null)
    const open = index !== null
    const item = index === null ? null : items[index]

    useLockScroll(open)

    useEffect(() => {
        const dialog = dialogRef.current

        if (!dialog) return

        if (open && !dialog.open) dialog.showModal()
        if (!open && dialog.open) dialog.close()
    }, [open])

    useEffect(() => {
        if (index === null) return

        const onKey = (event: KeyboardEvent): void => {
            if (event.key === 'ArrowRight') onChange((index + 1) % items.length)
            if (event.key === 'ArrowLeft') onChange((index - 1 + items.length) % items.length)
        }

        window.addEventListener('keydown', onKey)

        return () => window.removeEventListener('keydown', onKey)
    }, [index, items.length, onChange])

    const step = (direction: number): void => {
        if (index === null) return

        onChange((index + direction + items.length) % items.length)
    }

    const onTouchEnd = (event: TouchEvent<HTMLElement>): void => {
        const start = touchStart.current
        const end = event.changedTouches[0]?.clientX

        touchStart.current = null

        if (start === null || end === undefined || Math.abs(end - start) < 50) return

        step(end < start ? 1 : -1)
    }

    const ratio = item ? getDisplayRatio(item.photo) : 1

    return (
        <dialog
            ref={dialogRef}
            aria-label={item?.caption ?? GALLERY.title}
            onClose={() => onChange(null)}
            onClick={(event) => {
                if (event.target === event.currentTarget) onChange(null)
            }}
            className='m-auto max-h-none max-w-none overflow-visible border-0 bg-transparent p-0 text-white backdrop:bg-wine/85 backdrop:backdrop-blur-sm'
        >
            <button
                type='button'
                onClick={() => onChange(null)}
                aria-label={GALLERY.close}
                className='fixed right-4 top-4 z-10 grid size-11 place-items-center rounded-full bg-white/15 text-white backdrop-blur transition hover:bg-white/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white'
            >
                <X className='size-5' />
            </button>

            {item && (
                <figure
                    className='flex flex-col items-center px-4'
                    onTouchStart={(event) => {
                        touchStart.current = event.touches[0]?.clientX ?? null
                    }}
                    onTouchEnd={onTouchEnd}
                >
                    <div className='rounded-sm bg-white p-3 pb-4 shadow-2xl'>
                        <MemoryPhoto
                            key={item.photo.src}
                            photo={item.photo}
                            loading='eager'
                            style={{ width: `min(84vw, ${(ratio * 66).toFixed(2)}vh)` }}
                        />

                        <figcaption
                            className='mt-3 font-hand text-2xl leading-snug text-foreground'
                            style={{ maxWidth: `min(84vw, ${(ratio * 66).toFixed(2)}vh)` }}
                        >
                            {item.caption}
                        </figcaption>
                    </div>

                    <div className='mt-5 flex items-center gap-4'>
                        <button
                            type='button'
                            onClick={() => step(-1)}
                            aria-label={GALLERY.previous}
                            className='grid size-11 place-items-center rounded-full bg-white/15 backdrop-blur transition hover:bg-white/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white'
                        >
                            <ChevronLeft className='size-5' />
                        </button>

                        <p className='min-w-12 text-center text-sm font-bold tabular-nums text-white/80'>
                            {(index ?? 0) + 1} / {items.length}
                        </p>

                        <button
                            type='button'
                            onClick={() => step(1)}
                            aria-label={GALLERY.next}
                            className='grid size-11 place-items-center rounded-full bg-white/15 backdrop-blur transition hover:bg-white/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white'
                        >
                            <ChevronRight className='size-5' />
                        </button>
                    </div>
                </figure>
            )}
        </dialog>
    )
}

export default Lightbox
